import { HttpClient, HttpContext } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { DisplayUser, DisplayUserEmail, FolderDto, UpdateFolderBody } from '@nusign/picflow-backend-types';
import {
  BehaviorSubject,
  Observable,
  catchError,
  combineLatest,
  distinctUntilKeyChanged,
  filter,
  forkJoin,
  from,
  map,
  merge,
  of,
  shareReplay,
  skip,
  switchMap,
  take,
  tap,
} from 'rxjs';
import { ScreenSizes } from '../classes/screen-sizes.class';
import { standardMemoryCacheExpiry } from '../consts';
import { GROWTHBOOK } from '../growthbook';
import { FolderAccessLevel } from '../lib/access-level';
import { copy } from '../lib/copy';
import { forkJoinSafe } from '../lib/safe-fork-join';
import { unique } from '../lib/unique';
import { IGNORE_AUTH_ERRORS } from '../network-interceptor';
import { AppConfigService } from '../providers/config.service';
import { sortGalleriesAndFolders } from '../shared/pipes/galleries-sort-by.pipe';
import { FolderAccessLevelSubService } from './folder-access-level-sub.service';
import { FolderGalleryFilterService } from './folder-gallery-filter.service';
import { GalleryDisplayPreferencesService } from './gallery-display-preferences.service';
import { GalleryService } from './gallery.service';
import { ObservableDataLoaderService } from './observable-data-loader.service';
import { OrganizationService } from './organization.service';
import { PaginationService } from './pagination.service';
import { SharedWithMeFolderService } from './shared-with-me-folder.service';
import { StateService } from './state.service';
import { ToastService } from './toast.service';
import { UserGroupService } from './user-group.service';

@Injectable({
  providedIn: 'root',
})
export class FolderService {
  private http = inject(HttpClient);
  private config = inject(AppConfigService);
  private loader = inject(ObservableDataLoaderService);
  private state = inject(StateService);
  private toasts = inject(ToastService);
  private growthBook = inject(GROWTHBOOK);
  private galleryService = inject(GalleryService);
  private sharedWithMeFolderService = inject(SharedWithMeFolderService);
  private accessLevelService = inject(FolderAccessLevelSubService);
  private filterService = inject(FolderGalleryFilterService);
  private organizationService = inject(OrganizationService);
  private userGroupService = inject(UserGroupService);
  private galleryDisplayPreferencesService = inject(GalleryDisplayPreferencesService);
  private paginationService = inject(PaginationService);

  onFolderUpdated$ = new BehaviorSubject<FolderDto>(undefined);
  onFolderDeleted$ = new BehaviorSubject<FolderDto>(undefined);
  onFolderCreated$ = new BehaviorSubject<FolderDto>(undefined);

  private updateUpdater = this.onFolderUpdated$.subscribe(folder => {
    if (!folder) return;
    this.loader.update<FolderDto[]>('folders', folders => {
      const index = folders.findIndex(f => f.id === folder.id);
      if (index === -1) return folders;
      folders[index] = <FolderDto>{ ...folders[index], ...folder, last_changed_at: Math.floor(Date.now() / 1000) };
      return folders;
    });

    this.loader.updateFuzzy<FolderDto>(`folders_${folder.id}`, f => {
      if (!f) return folder;
      return { ...f, ...folder, last_changed_at: Math.floor(Date.now() / 1000) };
    });
  });

  private deleteUpdater = this.onFolderDeleted$.subscribe(folder => {
    this.loader.update<FolderDto[]>('folders', folders => {
      const folderChain = this.filterService.recursiveFilterFolders(folder.id, folders);
      return folders.filter(f => f.id !== folder.id && !folderChain.some(fc => fc.id === f.id));
    });
  });

  private createUpdater = this.onFolderCreated$.subscribe(folder => {
    this.loader.update<FolderDto[]>('folders', folders => {
      folders.push(folder);
      return folders;
    });
  });

  // Note: it is possible to pass a parent folder id but we don't because we also need to fetch subfolders and subsubfolders
  // Which is less efficient then just doing one requst for all folders
  public getFolders(
    parentFolderId?: string,
    options: { pageAfter?: string; limit?: number; parentFolder?: string; sorting?: string } = {}
  ): Observable<FolderDto[]> {
    if (this.state.sharedWithMeActiveTenant$.value) return this.getSharedWithMeFolders(parentFolderId);
    const activeTenant = this.state.activeTenant$?.value;
    if (!activeTenant) return of([]);
    return this.loader
      .get(
        `folders${options.pageAfter ? `_${options.pageAfter}` : ''}${options.limit ? `_${options.limit}` : ''}${
          options.parentFolder ? `_${options.parentFolder}` : ''
        }${options.sorting ? `_${options.sorting}` : ''}`,
        () => {
          const url = `${this.config.apiEndpoint}/folders${this.config.isTenantDomain ? `` : '?context=dashboard'}${options.pageAfter ? `&page_after=${options.pageAfter}` : ''}${options.limit ? `&limit=${options.limit}` : ''}${options.parentFolder ? `&parent_folder=${options.parentFolder}` : ''}${options.sorting ? `&sort[]=${options.sorting}` : ''}`;
          return this.http.get<{ data: FolderDto[] }>(url).pipe(map(resp => resp.data));
        },
        standardMemoryCacheExpiry,
        false
      )
      .pipe(
        map(folders => {
          if (!parentFolderId) return folders;
          if (parentFolderId === 'none') return folders.filter(f => !f.parent_folder);
          return folders.filter(f => f.parent_folder === parentFolderId);
        }),
        switchMap(folders => this.filterAndHandleNewFolders(folders, true))
      );
  }

  public getFoldersPaginated(
    parentFolder: string,
    next: BehaviorSubject<void>,
    options: {
      limit?: number;
      sorting?: string;
      includeSubfolderCount?: boolean;
    } = {}
  ): Observable<{ folders: FolderDto[]; has_more_next: boolean }> {
    const limit = options.limit || ScreenSizes.screenSize() === 'mobile' ? 12 : 24;
    if (!parentFolder) parentFolder = 'none';
    return this.state.activeTenant$.pipe(
      filter(Boolean),
      distinctUntilKeyChanged('id'),
      switchMap(activeTenant => {
        if (!activeTenant) return of(undefined);

        let allFolders: FolderDto[] = [];
        return this.galleryDisplayPreferencesService.displayPreferences$.pipe(
          filter(Boolean),
          switchMap(prefs => {
            allFolders = [];
            return next.pipe(
              switchMap(() => {
                const lastFolder = allFolders[allFolders.length - 1];
                const sorting = options.sorting || prefs.sorting;
                const pageAfter = this.paginationService.encodePageInfo(lastFolder, [sorting]);
                const url = `${this.config.apiEndpoint}/folders${this.config.isTenantDomain ? `` : '?context=dashboard'}${pageAfter ? `&page_after=${pageAfter}` : ''}${`&limit=${limit}`}&parent_folder=${parentFolder}${sorting ? `&sort[]=${sorting}` : ''}${options.includeSubfolderCount ? `&include[]=subfolders_count` : ''}`;
                return this.http.get<{ data: FolderDto[]; has_more_next: boolean }>(url).pipe(
                  map(resp => {
                    allFolders.push(...resp.data);
                    allFolders = unique(allFolders, 'id', true); // Ensure unique folders

                    return resp.has_more_next;
                  })
                );
              })
            );
          }),
          switchMap(has_more_next => {
            // Listen for folder updates and deletions
            return merge(
              this.onFolderUpdated$.pipe(
                skip(1),
                map(folder => ({ event: 'updated', folder }))
              ),
              this.onFolderDeleted$.pipe(
                skip(1),
                map(folder => ({ event: 'deleted', folder }))
              ),
              this.onFolderCreated$.pipe(
                skip(1),
                map(folder => ({ event: 'created', folder }))
              ),
              of({ event: 'initial', folder: undefined as FolderDto })
            ).pipe(
              switchMap(data => {
                if (data.event === 'initial') return of({ folders: allFolders, has_more_next });
                const updatedFolder = data.event === 'updated' ? data.folder : undefined;
                const deletedFolder = data.event === 'deleted' ? data.folder : undefined;
                const createdFolder = data.event === 'created' ? data.folder : undefined;

                return this.galleryDisplayPreferencesService.displayPreferences$.pipe(
                  filter(Boolean),
                  take(1),
                  map(prefs => {
                    if (updatedFolder) {
                      const folderMovedToOtherFolder = (updatedFolder.parent_folder || 'none') !== parentFolder;
                      if (folderMovedToOtherFolder) {
                        // If the folder was moved to another parent folder, we need to remove it from the current list
                        allFolders = allFolders.filter(f => f.id !== updatedFolder.id);
                      } else {
                        const index = allFolders.findIndex(f => f.id === updatedFolder.id);
                        if (index > -1) {
                          allFolders[index] = { ...allFolders[index], ...updatedFolder };
                        }
                      }
                    }
                    if (deletedFolder) {
                      allFolders = allFolders.filter(f => f.id !== deletedFolder.id);
                    }

                    if (createdFolder && (createdFolder.parent_folder || 'none') === parentFolder) {
                      allFolders.push(createdFolder);
                    }

                    // Re-sort the folders based on the current sorting preference
                    const sorting = options.sorting || prefs.sorting;
                    allFolders = sortGalleriesAndFolders(allFolders, false, sorting, false);

                    return {
                      folders: allFolders,
                      has_more_next: has_more_next,
                    };
                  })
                );
              })
            );
          })
        );
      })
    );
  }

  public getGroupFolders(groupId: string): Observable<FolderDto[]> {
    return combineLatest([this.getFolders(), this.userGroupService.getGroupFolders(groupId)]).pipe(
      map(([folders, groupFolders]) => folders.filter(f => groupFolders.some(gf => gf.folder === f.id)))
    );
  }

  public getPublicFolderFolders(folderId: string): Observable<FolderDto[]> {
    return this.loader
      .get(
        `folders_folder_${folderId}`,
        () =>
          this.http
            .get<{ data: FolderDto[] }>(`${this.config.apiEndpoint}/folders?folder=${folderId}`)
            .pipe(map(resp => resp.data.filter(f => f.parent_folder === folderId))),
        standardMemoryCacheExpiry,
        false
      )
      .pipe(switchMap(folders => this.filterAndHandleNewFolders(folders, false, true)));
  }

  public getAllFolderDescendants(folderId: string): Observable<FolderDto[]> {
    return this.getFolders().pipe(
      take(1),
      map(folders => this.filterService.recursiveFilterFolders(folderId, folders))
    );
  }

  private getFolderFromCache(folderId: string) {
    return forkJoin([
      this.loader.getFromCache<FolderDto[]>('folders').pipe(take(1)),
      this.loader.getFromCache<FolderDto[]>('/folders?shared').pipe(take(1)),
    ]).pipe(
      map(([folders, sharedWithMeFolders]) => {
        const folder = folders?.find(f => f.id === folderId);
        if (folder) return folder;
        const sharedWithMeFolder = sharedWithMeFolders?.find(f => f.id === folderId);
        if (sharedWithMeFolder) return sharedWithMeFolder;
      })
    );
  }

  public getFolder(
    id: string,
    options: { allowCached?: boolean; effectiveAccessLevels?: boolean; noCache?: boolean } = {}
  ): Observable<FolderDto> {
    if (options.allowCached && !options.effectiveAccessLevels) {
      return this.getFolderFromCache(id).pipe(
        switchMap(folder => {
          if (folder) return of(folder);
          return this.getFolder(id);
        })
      );
    }

    return this.loader.get(
      `folders_${id}${options.effectiveAccessLevels ? '_effective_access' : ''}${options.noCache ? Date.now().toString() : ''}`,
      () => {
        return this.http.get<FolderDto>(
          `${this.config.apiEndpoint}/folders/${id}${options.effectiveAccessLevels ? '?include[]=effective_access' : ''}`,
          {
            context: new HttpContext().set(IGNORE_AUTH_ERRORS, true),
          }
        );
      },
      standardMemoryCacheExpiry,
      false
    );
  }

  public createFolder(title: string, parentFolderId?: string) {
    const params = { title, parent_folder: parentFolderId, public_access: parentFolderId ? null : 'no_access' };
    return this.http.post<FolderDto>(`${this.config.apiEndpoint}/folders`, params).pipe(
      tap(folder => {
        this.onFolderCreated$.next(folder);
      })
    );
  }

  private wouldCauseCircularInheritence(folderId: string, newParentId: string) {
    if (!newParentId) return of(false);
    return this.getFolderChain(newParentId).pipe(map(folderChain => folderChain.some(f => f.id === folderId)));
  }

  public updateFolder(folderId: string, params: UpdateFolderBody) {
    return this.wouldCauseCircularInheritence(folderId, params.parent_folder).pipe(
      switchMap(circular => {
        if (circular) {
          this.toasts.add("Can't move Folder. Would create circlular inheritence chain", 'error');
          return of(undefined);
        }
        return this.http.patch<FolderDto>(`${this.config.apiEndpoint}/folders/${folderId}`, params).pipe(
          tap(resp => {
            this.onFolderUpdated$.next(resp);
          })
        );
      })
    );
  }

  public deleteFolder(folder: FolderDto) {
    return this.http.delete(`${this.config.apiEndpoint}/folders/${folder.id}`).pipe(
      tap(() => {
        this.onFolderDeleted$.next(folder);
      })
    );
  }

  public filterAndHandleNewFolders(folders: FolderDto[], ignorePublicAccessLevel: boolean, dontFilter?: boolean) {
    return of(folders).pipe(
      map(folder => unique(folder, 'id')),
      switchMap(folders => {
        if (dontFilter) return of(folders);
        if (this.growthBook.isOn('fine_grained_authorization')) {
          return of(folders);
        }
        return this.filterOutNoAccess(folders, { ignorePublicAccessLevel });
      })
    );
  }

  private filterOutNoAccess(
    folders: FolderDto[],
    options: { ignorePublicAccessLevel?: boolean } = {}
  ): Observable<FolderDto[]> {
    return this.getSharedWithMeFolders().pipe(
      take(1),
      switchMap(sharedWithMeFolders => {
        return forkJoinSafe(
          folders.map(folder => {
            return this.getFolderChain(folder.id).pipe(
              switchMap(folderChain => {
                return from(
                  this.accessLevelService.getAccessLevel(folder, folderChain, sharedWithMeFolders, options)
                ).pipe(
                  map(accessLevel => {
                    if (accessLevel !== 'no_access') return folder;
                    console.debug('filtered out folder', folder.id, 'because of access level');
                  })
                );
              })
            );
          })
        ).pipe(map(galleries => galleries.filter(Boolean)));
      })
    );
  }

  private sharedWithMeFolders$ = this.state.sharedWithMeActiveTenant$.pipe(
    switchMap(() => {
      if (!this.state.user) return of([] as (FolderDto & { access_level: FolderAccessLevel })[]);
      return this.sharedWithMeFolderService.getSharedWithMeFoldersRaw().pipe(
        map(folders => copy(folders)), // maybe needed
        switchMap(folders => this.filterAndHandleNewFolders(folders, false, true)),
        map(folders => {
          const activeTenant = this.state.activeTenant$?.value;
          const tenantId = this.state.sharedWithMeActiveTenant$.value?.id || activeTenant?.id;

          if (!tenantId) return folders as (FolderDto & { access_level: FolderAccessLevel })[];

          return folders.filter(f => {
            const folderTenantId = f.tenant?.['id'] || f.tenant;
            return folderTenantId === tenantId;
          }) as (FolderDto & { access_level: FolderAccessLevel })[];
        })
      );
    }),
    shareReplay(1)
  );

  public getSharedWithMeFolders(parentFolderId?: string) {
    if (!this.state.user) return of([] as (FolderDto & { access_level: FolderAccessLevel })[]);
    return this.sharedWithMeFolders$.pipe(
      map(folders => {
        if (!parentFolderId) return folders;
        if (parentFolderId !== 'none') return folders.filter(f => f.parent_folder === parentFolderId);
        return folders.filter(folder => {
          if (!folder.parent_folder) return true; // Top-level folders
          return !folders.some(f => f.id === folder.parent_folder); // Only show top-level folders that are not subfolders of other shared folders
        });
      })
    );
  }

  public getFolderChain(folderId: string, chain: FolderDto[] = []): Observable<FolderDto[]> {
    if (!folderId) return of(chain);
    return this.getFolder(folderId, { allowCached: true }).pipe(
      take(1),
      switchMap(folder => {
        if (chain.some(f => f.id === folder.id)) {
          console.error('circular folder chain detected', chain);
          return of(chain);
        }
        chain.push(folder);
        if (!folder.parent_folder) return of(chain);
        return this.getFolderChain(folder.parent_folder as string, chain);
      }),
      catchError(err => {
        console.error('error getting folder chain', err);
        return of(chain);
      })
    );
  }

  public getFolderOwner(folderId: string, isTeamMember: boolean): Observable<DisplayUserEmail> {
    return this.loader.get(
      `folder_owner_${folderId}`,
      () => {
        const url = `${this.config.apiEndpoint}/folders/${folderId}?expand[]=created_by`;
        return this.http
          .get<
            FolderDto & { created_by: DisplayUser }
          >(url, { context: new HttpContext().set(IGNORE_AUTH_ERRORS, true) })
          .pipe(
            switchMap(resp => {
              return (isTeamMember ? this.organizationService.getMembers() : of([])).pipe(
                map(members => {
                  return {
                    ...(resp.created_by as DisplayUser),
                    email: members.find(m => m.id === resp.created_by.id)?.email,
                  };
                })
              );
            })
          );
      },
      standardMemoryCacheExpiry,
      false
    );
  }
}
